import React, { useEffect, useState } from "react";
import './index.scss';
import If from "../If";
import axiosClient from "../../../redux/services/apiClient";
import { toast } from "react-toastify"; 

type LegacyPinWithHelpModalProps = {
  defaultPin?: string;

  // Let parent know the result of validation
  onValidated?: (resp: any | null) => void;
  
  // Notify parent whenever PIN text changes
  onPinChange?: (pin: string) => void;

  // Help modal control (optional)
  helpOpen?: boolean;             // controlled modal open state
  onHelpOpenChange?: (open: boolean) => void;

  // Text overrides
  helperText?: string;            // "Enter PIN here to validate"
  submitLabel?: string;           // "Enter"

  // Help modal content overrides
  helpTitle?: React.ReactNode;    // modal <h2>
  helpBody?: React.ReactNode;     // modal body
  helpTitleId?: string;           // aria-labelledby id (default "pin-help-title")

  // “I don't have a PIN”
  onNeedPinClick?: () => void;    // if provided, uses button; else link
  needPinHref?: string;           // default "https://legacytouch.com/pages/how-it-works"

  // IDs & styling
  inputId?: string;               // default "desktop-PIN"
  submitId?: string;              // default "desktop-PIN-submit"
  className?: string;             // extra classes for outer container
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
};

const LegacyPinWithHelpModal: React.FC<LegacyPinWithHelpModalProps> = ({
  // PIN
  defaultPin,
  onValidated,
  onPinChange,

  // Help modal control
  helpOpen,
  onHelpOpenChange,

  // Text
  helperText = "Enter PIN here to validate",
  submitLabel = "Enter",

  // Modal content
  helpTitle = "The Legacy Touch PIN",
  helpBody,
  helpTitleId = "pin-help-title",

  // Need PIN
  onNeedPinClick,
  needPinHref = "https://legacytouch.com/pages/how-it-works",

  // IDs & styling
  inputId = "mobile-PIN",
  submitId = "mobile-PIN-submit",
  className = "",
  disabled = false,
  autoFocus = false,
  placeholder = "Enter your PIN",
}) => {
  const [pin, setPin] = useState<string>(defaultPin ?? "");
  const [pinResponse, setPinResponse] = useState<any>(null);
  const [retryRequested, setRetryRequested] = useState(false);


  // ----- Help modal state (controlled or uncontrolled) -----
  const isHelpControlled = helpOpen !== undefined;
  const [internalHelpOpen, setInternalHelpOpen] = useState(false);
  const open = isHelpControlled ? (helpOpen as boolean) : internalHelpOpen;

  // ----- Fingerprint Success Modal -----
  const [fingerprintModalOpen, setFingerprintModalOpen] = useState(false);

  const setOpen = (next: boolean) => {
    if (isHelpControlled) onHelpOpenChange?.(next);
    else setInternalHelpOpen(next);
  };

  // ESC to close & lock scroll while modal open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

 // If no default provided, try sessionStorage and auto-validate it
  useEffect(() => {
    if (defaultPin) return;
    const stored = sessionStorage.getItem("legacypin1");
    if (stored) {
      setPin(stored);
      validatePin(stored);
    }
  }, []);

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPin(value);
    onPinChange?.(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validatePin();
    }
  };

  const validatePin = async (maybePin?: string) => {
    const value = (maybePin ?? pin).trim();
    if (!value) return;

    try {
      const res = await axiosClient.post(
        `https://ecomm-api.legacytouch.com/api/1.0/decedents/${value}`
      );
      const response = { ...res.data, pin: value };
      setPinResponse(response);
      onValidated?.(response);

      if (response.isValid) {
        sessionStorage.setItem("legacypin1", value);
        
        const hasShown = localStorage.getItem("fingerprintSuccessShown");
        if (!hasShown) {
          setFingerprintModalOpen(true);
          localStorage.setItem("fingerprintSuccessShown", "true");
        }
      } else {
        toast.error("Invalid PIN");
      }
    } catch {
      toast.error("Could not validate PIN");
      setPinResponse(null);
      onValidated?.(null);
    }
  };

  // Default help body if none provided
  const defaultHelpBody = (
    <>
      <p className="tw-text-center tw-mb-5">
        The Legacy Touch PIN is what uniquely identifies fingerprints (or hand, foot, or paw prints) that are already in our
        system. A PIN is required to place an order for any of our Fingerprint Jewelry or Keepsakes. If you don't have a PIN,
        but you do have prints that you wish to use, close this window and click the tab that says "I Need a PIN."
      </p>
      <p className="tw-text-center">
        <i>
          <b>Please note:</b> A PIN does not allow you to see or download digital prints. The prints shown on our website are
          sample prints and are for illustrative purposes only. Your Loved One's fingerprint images are housed in secure
          digital storage and are not available for general viewing. If you need access to a copy of your Loved One's prints,
          please{" "}
          <a
            href="https://legacytouch.com/pages/contact-us"
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: 700 }}
          >
            Contact our Customer Service team
          </a>.
        </i>
      </p>
    </>
  );

  return (
    <>
      {/* PIN block */}
      <div className={`legacy-pin-container tw-mt-6 ${className}`}>
        <div className="tw-flex">
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#52525B" }}>
            Verify fingerprint
          </div>

          <div
            style={{
              marginLeft: "auto",
              textAlign: "right",
              textDecoration: "italic",
              fontSize: "12px",
              color: "#71717A",
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="tw-underline tw-italic tw-text-inherit tw-bg-transparent tw-border-0 tw-cursor-pointer"
            >
              What's this?
            </button>
          </div>
        </div>

        <If condition={!pinResponse || !pinResponse?.isValid || retryRequested}>
          <div className="tw-flex tw-mt-4 tw-flex-col" style={{ alignItems: "center" }}>
            <input
              name="PIN"
              id={inputId}
              type="text"
              value={pin}
              onChange={handlePinChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              autoFocus={autoFocus}
              placeholder={placeholder}
              style={{ 
                height: "41px", 
                border: "solid 1px #D0D5DD", 
                padding: '0 10px',
                width: '70%'
              }}
            />

            <button
              id={submitId}
              type="button"
              onClick={() => validatePin()}
              disabled={disabled}
              style={{
                marginTop: "10px",
                borderRadius: "8px",
                padding: "8px 18px",
                backgroundColor: '#004071',
                color: "#FFFFFF",
                border: "solid 1px #D0D5DD",
                opacity: disabled ? 0.6 : 1,
                width: '70%'
              }}
            >
              {submitLabel}
            </button>
          </div>
        </If>

        <If condition={pinResponse && pinResponse?.isValid}>
          <div className="tw-mt-4 tw-justify-center tw-flex" style={{ fontSize: "14px" }}>
            <div style={{ backgroundColor: "#E6FFF3", padding: "5px", borderRadius: "99px", marginRight: "5px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> <path d="M7.5 11.3497L10.5 15.3497L16.5 9.34998" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> </svg>
            </div>
            <div style={{ paddingTop: "1px", color: "rgb(82, 82, 91)" }}>Prints belong to <b>{pinResponse?.firstName} {pinResponse?.lastName}</b></div>
          </div>

          <div className="tw-mt-3 tw-text-center">
            <button
              type="button"
              onClick={() => {
                setRetryRequested(true);
                setPin("");
                localStorage.removeItem("fingerprintSuccessShown");
              }}
              className="tw-underline tw-text-inherit tw-bg-transparent tw-border-0 tw-cursor-pointer"
              style={{ fontSize: "14px", color: "rgb(113, 113, 122)" }}
            >
              Try Again
            </button>
          </div>
        </If>
        <If condition={pinResponse && !pinResponse?.isValid}>
          <div className="tw-mt-2 tw-justify-center tw-flex" style={{ fontSize: "12px" }}>
            <div style={{ backgroundColor: "#FFE6E6", padding: "5px", borderRadius: "99px", marginRight: "5px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> <path d="M15 9L9 15" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> <path d="M15 15L9 9" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> </svg>
            </div>
            <div style={{ paddingTop: "3px" }}>Invalid PIN</div>
          </div>
        </If>

        <If condition={!pinResponse || !pinResponse?.isValid}>
          <div className="tw-mt-4 tw-text-center" style={{ fontSize: "16px", color: "#71717A" }}>
            {onNeedPinClick ? (
              <button
                type="button"
                onClick={onNeedPinClick}
                className="tw-underline tw-text-inherit tw-bg-transparent tw-border-0 tw-cursor-pointer"
              >
                I don't have a PIN
              </button>
            ) : (
              <a href={needPinHref} target="_blank" rel="noreferrer" className="tw-underline">
                I don't have a PIN
              </a>
            )}
          </div>
        </If>
      </div>

      {/* Help Modal (with overlay) */}
      {open && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-[1000]"
          onClick={(e) => {
            if (e.currentTarget === e.target) setOpen(false); // backdrop click closes
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={helpTitleId}
        >
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-w-[min(92vw,520px)] tw-p-6">
            <div className="tw-flex tw-items-start tw-justify-center tw-pb-4 tw-mb-4">
              <h2 id={helpTitleId} className="tw-text-lg tw-font-semibold tw-text-gray-900">
                {helpTitle}
              </h2>
            </div>

            <div
              className="tw-text-sm tw-text-gray-700 tw-py-4"
              style={{ borderTop: "solid 1px #ccc", borderBottom: "solid 1px #ccc", marginBottom: "1rem" }}
            >
              {helpBody ?? defaultHelpBody}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="tw-bg-gray-100 tw-text-gray-800 tw-rounded-lg tw-px-4 tw-py-2"
                style={{ backgroundColor: "#002f6c", color: "#fff" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fingerprint Success Modal (with overlay) */}
      {fingerprintModalOpen && (
        <div
          className="lt-fingerprint-modal tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center"
          onClick={(e) => {
            if (e.currentTarget === e.target) setFingerprintModalOpen(false); // backdrop click closes
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-container tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-6 tw-relative" style={{ display: "flex", alignItems: "center"}}>
            <button
              type="button"
              onClick={() => setFingerprintModalOpen(false)}
              aria-label="Close"
              className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
            >
              ✕
            </button>
            <div className="info-container">
              <div className="tw-my-4 tw-justify-left tw-flex" style={{ fontSize: "16px" }}>
                <div style={{ backgroundColor: "#E6FFF3", padding: "5px", borderRadius: "99px", marginRight: "5px" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> <path d="M7.5 11.3497L10.5 15.3497L16.5 9.34998" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                </div>
                <div style={{ paddingTop: "1px", color: "rgb(82, 82, 91)" }}>Prints belong to <b>{pinResponse?.firstName} {pinResponse?.lastName}</b></div>
              </div>

              <div className="tw-flex tw-items-start">
                <h2 className="tw-font-semibold tw-text-gray-900">
                  Fingerprint Entered Successfully
                </h2>
              </div>

              <div className="mobile-video-container">
                <video
                  src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/Engraving+How+To+compressed.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="tw-w-full tw-rounded-lg tw-shadow-lg"
                />
              </div>

              <div
                className="text-container tw-text-sm tw-text-gray-700"
              >
                <p style={{ fontSize: "16px" }}><b>Next:</b></p>
                <p style={{ fontSize: "16px" }}>Choose fingerprint location — as an <b>outside feature</b> or an <b>inside engraving</b>.</p>
              </div>

              <div className="tw-flex tw-justify-end tw-gap-2">
                <button
                  type="button"
                  onClick={() => setFingerprintModalOpen(false)}
                  className="tw-bg-gray-100 tw-text-gray-800 tw-rounded-lg tw-px-4 tw-py-2 tw-w-full"
                  style={{ backgroundColor: "#002f6c", color: "#fff", borderRadius: "20px" }}
                >
                  Continue
                </button>
              </div>
            </div>
            <div className="tw-p-0 desktop-video-container" style={{ flex: "1 1 0px"}}>
              <video
                src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/Engraving+How+To+compressed.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
                className="tw-w-full tw-rounded-lg tw-shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LegacyPinWithHelpModal;
